import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { LEAVE_TYPES, label } from "@/lib/constants";
import { fmtDate, initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Plane, Check, X, Users, CalendarDays } from "lucide-react";

const STATUS_CHIP = { pending: "bg-amber-100 text-amber-700 border-amber-200", approved: "bg-emerald-100 text-emerald-700 border-emerald-200", rejected: "bg-rose-100 text-rose-700 border-rose-200" };
const STATUS_LABEL = { pending: "Beklemede", approved: "Onaylandı", rejected: "Reddedildi" };

const dayCount = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start.slice(0, 10)), e = new Date(end.slice(0, 10));
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
};

export default function Leave() {
  const qc = useQueryClient();
  const { user, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "annual", start_date: "", end_date: "", reason: "", approver_id: "" });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data: leaves = [], isLoading } = useQuery({ queryKey: ["leave-requests"], queryFn: async () => { const d = (await api.get("/leave-requests")).data; return Array.isArray(d) ? d : []; } });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data });
  const { data: cap } = useQuery({ queryKey: ["leave-capacity"], queryFn: async () => (await api.get("/leave/capacity")).data });

  const canApprove = can("view_reports");

  const create = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Başlangıç ve bitiş tarihi gerekli"); return; }
    if (form.end_date < form.start_date) { toast.error("Bitiş tarihi başlangıçtan önce olamaz"); return; }
    try {
      await api.post("/leave-requests", { ...form, approver_id: form.approver_id || null });
      toast.success("İzin talebi oluşturuldu");
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      qc.invalidateQueries({ queryKey: ["leave-capacity"] });
      setOpen(false); setForm({ leave_type: "annual", start_date: "", end_date: "", reason: "", approver_id: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const decide = async (id, status, reason = "") => {
    try {
      await api.patch(`/leave-requests/${id}`, { status, reason });
      toast.success(status === "approved" ? "İzin onaylandı" : "İzin reddedildi");
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      qc.invalidateQueries({ queryKey: ["leave-capacity"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openReject = (l) => { setRejectReason(""); setRejectTarget(l); };
  const confirmReject = () => { if (rejectTarget) { decide(rejectTarget.id, "rejected", rejectReason); setRejectTarget(null); } };

  const mine = useMemo(() => leaves.filter((l) => l.user_id === user?.id), [leaves, user]);
  const pending = useMemo(() => leaves.filter((l) => l.status === "pending"), [leaves]);
  const visible = canApprove ? leaves : mine;

  const Row = ({ l }) => (
    <div className="flex items-center gap-3 p-3.5 border-b last:border-0" data-testid={`leave-row-${l.id}`}>
      <Avatar className="h-8 w-8"><AvatarFallback className="text-[10px]">{initials(l.user_name)}</AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{l.user_name} <span className="text-muted-foreground font-normal">• {label(LEAVE_TYPES, l.leave_type)}</span></p>
        <p className="text-xs text-muted-foreground">{fmtDate(l.start_date)} — {fmtDate(l.end_date)} • {dayCount(l.start_date, l.end_date)} gün{l.reason ? ` • ${l.reason}` : ""}</p>
        {l.status === "rejected" && l.reject_reason && <p className="text-xs text-rose-600 mt-0.5">Red gerekçesi: {l.reject_reason}</p>}
      </div>
      <Badge variant="outline" className={`text-[10px] ${STATUS_CHIP[l.status]}`}>{STATUS_LABEL[l.status]}</Badge>
      {l.status === "pending" && canApprove && (
        <div className="flex gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600" onClick={() => decide(l.id, "approved")} data-testid={`leave-approve-${l.id}`}><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8 text-rose-600" onClick={() => openReject(l)} data-testid={`leave-reject-${l.id}`}><X className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title="İzinler" subtitle={`${pending.length} bekleyen izin talebi`}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-leave-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni İzin Talebi</Button>} />

      {isLoading ? <ListSkeleton /> : (
        <Tabs defaultValue="requests">
          <TabsList>
            <TabsTrigger value="requests" data-testid="leave-tab-requests"><Plane className="h-4 w-4 mr-1.5" /> İzin Talepleri</TabsTrigger>
            <TabsTrigger value="capacity" data-testid="leave-tab-capacity"><Users className="h-4 w-4 mr-1.5" /> Kapasite Planlama</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-4">
            <Card data-testid="leave-list">
              {visible.length ? visible.map((l) => <Row key={l.id} l={l} />)
                : <EmptyState icon={Plane} title="İzin talebi yok" description="Yeni bir izin talebi oluşturun." />}
            </Card>
          </TabsContent>

          <TabsContent value="capacity" className="mt-4 space-y-4">
            {cap?.on_leave_today?.length > 0 && (
              <Card className="p-4" data-testid="on-leave-today">
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><CalendarDays className="h-4 w-4 text-primary" /> Bugün izinde olanlar</div>
                <div className="flex flex-wrap gap-1.5">
                  {cap.on_leave_today.map((n, i) => <Badge key={i} variant="secondary" className="text-xs">{n}</Badge>)}
                </div>
              </Card>
            )}
            <Card data-testid="capacity-table" className="p-1.5">
              {(() => { const maxTasks = Math.max(1, ...(cap?.capacity || []).map((x) => x.task_count)); return (cap?.capacity || []).map((r) => (
                <div key={r.user_id} className="flex flex-wrap items-center gap-x-6 gap-y-2 px-3 py-3 border-b last:border-0" data-testid={`capacity-row-${r.user_id}`}>
                  <div className="flex items-center gap-2.5 min-w-0 flex-1 basis-full sm:basis-56">
                    <Avatar className="h-8 w-8 shrink-0"><AvatarFallback className="text-[10px]">{initials(r.name)}</AvatarFallback></Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.title || r.role_label}</p>
                    </div>
                    <div className="ml-auto sm:hidden shrink-0">
                      {r.on_leave
                        ? <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">İzinde</Badge>
                        : <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Müsait</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Açık görev</span>
                    <div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(r.task_count / maxTasks) * 100}%` }} /></div>
                    <span className="text-sm font-semibold tabular-nums w-5 text-right">{r.task_count}</span>
                  </div>
                  <div className="text-sm tabular-nums whitespace-nowrap">
                    <span className="text-xs text-muted-foreground mr-1.5">Yıllık izin</span>
                    <span className="font-medium">{r.used_annual}</span>
                    <span className="text-muted-foreground"> / {r.remaining_annual} gün</span>
                  </div>
                  <div className="hidden sm:block ml-auto shrink-0">
                    {r.on_leave
                      ? <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">İzinde</Badge>
                      : <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Müsait</Badge>}
                  </div>
                </div>
              )); })()}
              {!(cap?.capacity || []).length && <EmptyState icon={Users} title="Veri yok" />}
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leave-dialog">
          <DialogHeader><DialogTitle>Yeni İzin Talebi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>İzin Türü</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="leave-start-input" /></div>
              <div><Label>Bitiş</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" data-testid="leave-end-input" /></div>
            </div>
            {form.start_date && form.end_date && form.end_date >= form.start_date && (
              <p className="text-xs text-muted-foreground">Toplam <span className="font-semibold text-foreground">{dayCount(form.start_date, form.end_date)} gün</span> izin talep ediliyor.</p>
            )}
            <div><Label>Onaylayan</Label>
              <Select value={form.approver_id || "none"} onValueChange={(v) => setForm({ ...form, approver_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="leave-approver-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Yok</SelectItem>{members.filter((m) => m.id !== user?.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Açıklama</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" placeholder="İsteğe bağlı" data-testid="leave-reason-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button onClick={create} data-testid="leave-save-button">Gönder</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent data-testid="leave-reject-dialog">
          <DialogHeader><DialogTitle>İzin talebini reddet</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Red gerekçesi (isteğe bağlı)</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
              placeholder="Örn. Bu tarihlerde ekip kapasitesi yetersiz..." data-testid="leave-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>İptal</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmReject} data-testid="leave-reject-confirm">Reddet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
